import { Container, Pagination, usePagination } from '@zoom/components';
import { startOfToday } from 'date-fns';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AdherenceHistoryBottomPanel } from './AdherenceHistoryBottomPanel';
import { AdherenceHistoryHeader } from './AdherenceHistoryHeader';
import { defaultRangeFilter } from './AdherenceHistoryRangeFilterTile';
import { AdherenceHistorySidebar, defaultSidebarFilter } from './AdherenceHistorySidebar';
import { AdherenceHistoryTable } from './AdherenceHistoryTable';
import { CONTAINER_TAG, TITLE_TAG } from './constants';
import { renderRadioTileSubtitleHeader, renderRangeTileSubtitleHeader } from './utils';
import { useAuth } from '../../app/AuthorizationProvider';
import { ActiveFilters as ActiveFiltersBase } from '../../components/ActiveFilters/ActiveFilters';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { useBottomPanel } from '../../components/layout/BottomPanel';
import { Content } from '../../components/layout/Content';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  AdherenceHistoryItem,
  AdherenceHistorySearchCriteriaInput,
  Team,
  useGetAdherenceHistoryStartDateQuery,
  useGetAdherenceLastImportQuery,
  useGetSupervisedAdherenceHistoryQuery,
  useGetSupervisedTeamsQuery,
  useGetTeamsAllQuery,
} from '../../graphql/components';
import { SupervisedTeamsResult } from '../../graphql/types';
import { recordBy } from '../../utils/array';
import { getTimeZone } from '../../utils/date';
import { createEndOfDateRange } from '../../utils/dateRange';
import { useFilter } from '../../utils/filter';

import type { AdherenceHistoryHeaderFilterType, AdherenceHistorySidebarFilterState } from './types';

const defaultAdherenceHistoryHeaderFilter = {
  startDateTime: startOfToday(),
  endDateTime: startOfToday(),
};

type AdherenceHistoryItemState = {
  endDateTime: Date;
  item: AdherenceHistoryItem;
  startDateTime: Date;
} | null;

export const AdherenceHistoryScene: React.FC = () => {
  const { t } = useTranslation();
  const { locale, userId } = useAuth();
  const { closeBottomPanel, isBottomPanelOpen, openBottomPanel } = useBottomPanel();
  const { closeSidebar, isSidebarOpen, openSidebar } = useSidebar();

  const [items, setItems] = useState(0);
  const [lastDataImportDate, setLastDataImportDate] = useState<string | null>(null);
  const [adherenceHistoryItemToView, setAdherenceHistoryItemToView] = useState<AdherenceHistoryItemState>(null);

  // Sidebar Filter Accordion open tiles state
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  const { activePage, itemsPerPage, offset, totalPages, setPage } = usePagination(items);
  const { filter, setFilter } = useFilter<AdherenceHistoryHeaderFilterType>(defaultAdherenceHistoryHeaderFilter);

  const {
    data: teamsData,
    loading: teamsLoading,
    error: teamsError,
  } = useGetSupervisedTeamsQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
    },
  });

  const {
    data: teamsAllData,
    loading: teamsAllLoading,
    error: teamsAllError,
  } = useGetTeamsAllQuery({
    fetchPolicy: 'network-only',
  });

  const { filter: sidebarFilter, setFilter: setSidebarFilter } =
    useFilter<AdherenceHistorySidebarFilterState>(defaultSidebarFilter);

  const {
    data: startDateData,
    error: startDateError,
    loading: startDateLoading,
    refetch: startDateDataRefetch,
  } = useGetAdherenceHistoryStartDateQuery();

  const { loading: lastImportLoading, refetch: refetchLastImport } = useGetAdherenceLastImportQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ adherenceLastImport }) => {
      setLastDataImportDate(adherenceLastImport.dateTime ?? null);
    },
    variables: {
      status: 0,
    },
  });

  const adherenceHistorySearchCriteria: AdherenceHistorySearchCriteriaInput = {
    agentName: filter.agentName?.trim() ?? undefined,
    dateRange: {
      startDateTime: filter.startDateTime.toISOString(),
      endDateTime: createEndOfDateRange(filter.endDateTime).toISOString(),
    },
    first: offset,
    max: itemsPerPage,
    timezone: getTimeZone(),
    teamIds: sidebarFilter.teamIds ? [...Object.keys(sidebarFilter.teamIds)] : [],
    filter: {
      adherenceEventsRange: equals(sidebarFilter.adherenceEventsRange, defaultRangeFilter)
        ? undefined
        : sidebarFilter.adherenceEventsRange,
      conformanceRange: equals(sidebarFilter.conformanceRange, defaultRangeFilter)
        ? undefined
        : sidebarFilter.conformanceRange,
      inAdherenceRange: equals(sidebarFilter.inAdherenceRange, defaultRangeFilter)
        ? undefined
        : sidebarFilter.inAdherenceRange,
      earlyLeaves: typeof sidebarFilter.earlyLeaves === 'string' ? sidebarFilter.earlyLeaves === 'true' : undefined,
      includeInactiveAgents:
        typeof sidebarFilter.includeInactiveAgents === 'string'
          ? sidebarFilter.includeInactiveAgents === 'true'
          : undefined,
      lateArrivals: typeof sidebarFilter.lateArrivals === 'string' ? sidebarFilter.lateArrivals === 'true' : undefined,
    },
  };

  const handleCloseHistoryItemToView = () => {
    setAdherenceHistoryItemToView(null);
    closeBottomPanel();
  };

  const {
    data: adherenceHistoryResult,
    refetch: adherenceHistoryRefetch,
    loading: adherenceHistoryLoading,
    error: adherenceHistoryError,
  } = useGetSupervisedAdherenceHistoryQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      setItems(data?.supervisedAdherenceHistory?.totalCount ?? 0);

      if (adherenceHistoryItemToView) {
        const historyItem = data?.supervisedAdherenceHistory?.adherenceHistoryList.find(
          (item) => item.agentId === adherenceHistoryItemToView.item.agentId
        );

        // Bottom panel needs to be closed when no raw data are available for selected agent
        if (!historyItem || !historyItem.adherenceRawDataAvailable) {
          handleCloseHistoryItemToView();
        } else {
          // Let the content of bottom panel to be updated
          setAdherenceHistoryItemToView({
            endDateTime: createEndOfDateRange(filter.endDateTime),
            item: historyItem,
            startDateTime: filter.startDateTime,
          });
        }
      }
    },
    variables: {
      supervisorId: userId,
      adherenceHistorySearchCriteria,
    },
  });

  const adherenceHistory: AdherenceHistoryItem[] =
    adherenceHistoryResult?.supervisedAdherenceHistory.adherenceHistoryList ?? [];

  // All teams used in table view
  const teamsAll: Record<string, Team> = recordBy(teamsAllData?.teams ?? [], 'id');

  const isOneDaySelected = filter.startDateTime.getTime() === filter.endDateTime.getTime();

  const loading = startDateLoading || adherenceHistoryLoading || teamsLoading || teamsAllLoading;
  const error = startDateError || adherenceHistoryError || teamsError || teamsAllError;

  const onFilterSubmit = ({ agentName, startDateTime, endDateTime }: AdherenceHistoryHeaderFilterType) => {
    const values = {
      startDateTime,
      endDateTime,
      agentName: agentName?.trim(),
    };

    setFilter(values);
    setPage(1);
  };

  const handleViewAdherenceHistoryItem = (adherenceHistoryItem: AdherenceHistoryItem) => {
    if (adherenceHistoryItem.agentId !== adherenceHistoryItemToView?.item?.agentId) {
      setAdherenceHistoryItemToView({
        endDateTime: createEndOfDateRange(filter.endDateTime),
        item: adherenceHistoryItem,
        startDateTime: filter.startDateTime,
      });
      openBottomPanel();
    } else {
      handleCloseHistoryItemToView();
    }
  };

  const handleBottomPanel = () => {
    if (isBottomPanelOpen) {
      setAdherenceHistoryItemToView(null);
      closeBottomPanel();
    }
  };

  const handleFilterSidebar = () => {
    setPreOpenedItemIds([]);

    if (isSidebarOpen) {
      closeSidebar();
    } else {
      openSidebar();
      handleBottomPanel();
    }
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    handleBottomPanel();

    setPreOpenedItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setPreOpenedItemIds([]);
  };

  const handleRefresh = async () => {
    const { data } = await refetchLastImport({
      status: 0,
    });

    if (data.adherenceLastImport.dateTime !== lastDataImportDate) {
      await adherenceHistoryRefetch({ adherenceHistorySearchCriteria });
    }

    /**
     *  Not coupled with adherenceLastImport.dateTime
     *  Re-fetching only after edge-case minStartDate null case
     *  as when minStartDate value was correctly defined no changes are expected in future
     * */
    if (startDateData?.adherenceHistoryStartDate?.dateTime === null) {
      await startDateDataRefetch();
    }
  };

  const renderHeader = () => ({
    actionBar: (
      <AdherenceHistoryHeader
        t={t}
        onRefresh={handleRefresh}
        onSubmit={onFilterSubmit}
        toggleSidebar={handleFilterSidebar}
        isSidebarOpen={isSidebarOpen}
        minStartDate={startDateData?.adherenceHistoryStartDate?.dateTime || '0'} // TODO: Remove ?. before dateTime in final build
        lastDataImport={lastDataImportDate}
      />
    ),
    testId: TITLE_TAG,
    title: t('scheduleAdherence.adherenceHistory'),
    titleSlug: 'adherence-history',
  });

  const ActiveFilters: React.FC<{ isFixed?: boolean }> = ({ isFixed }) => (
    <ActiveFiltersBase
      config={[
        {
          dataTestId: 'adherence-history-active-sidebar-filter-teamIds',
          defaultValue: {},
          id: '1',
          key: 'teamIds',
          label: t('team.team', { count: 2 }),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-adherenceEventsRange',
          defaultValue: defaultRangeFilter,
          id: '2',
          key: 'adherenceEventsRange',
          label: t('scheduleAdherence.outOfAdherenceEvents'),
          nameFormatter: (value) => renderRangeTileSubtitleHeader(value, t, locale, false),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-inAdherenceRange',
          defaultValue: defaultRangeFilter,
          id: '3',
          key: 'inAdherenceRange',
          label: t('scheduleAdherence.inAdherence'),
          nameFormatter: (value) => renderRangeTileSubtitleHeader(value, t, locale, true),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-conformanceRange',
          defaultValue: defaultRangeFilter,
          id: '4',
          key: 'conformanceRange',
          label: t('scheduleAdherence.conformance'),
          nameFormatter: (value) => renderRangeTileSubtitleHeader(value, t, locale, true),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-lateArrivals',
          id: '5',
          key: 'lateArrivals',
          label: t('scheduleAdherence.lateArrivals', { count: 2 }),
          nameFormatter: (value) => renderRadioTileSubtitleHeader('lateArrivals', value, t),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-earlyLeaves',
          id: '6',
          key: 'earlyLeaves',
          label: t('scheduleAdherence.earlyLeaves', { count: 2 }),
          nameFormatter: (value) => renderRadioTileSubtitleHeader('earlyLeaves', value, t),
        },
        {
          dataTestId: 'adherence-history-active-sidebar-filter-includeInactiveAgents',
          id: '7',
          key: 'includeInactiveAgents',
          label: t('scheduleAdherence.inactivePersons'),
          nameFormatter: (value) => renderRadioTileSubtitleHeader('includeInactiveAgents', value, t),
        },
      ]}
      isFixed={isFixed}
      sidebarFilter={sidebarFilter}
      setSidebarFilter={setSidebarFilter}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      cancelFilterSidebar={handleCancelFilterSidebar}
    />
  );

  return (
    <Content
      bottomPanelContent={
        adherenceHistoryItemToView ? (
          <AdherenceHistoryBottomPanel
            adherenceHistoryItem={adherenceHistoryItemToView.item}
            lastDataImportDate={lastDataImportDate}
            startDateTime={adherenceHistoryItemToView.startDateTime}
            endDateTime={adherenceHistoryItemToView.endDateTime}
            onClose={handleCloseHistoryItemToView}
            teams={teamsAll}
          />
        ) : null
      }
      header={renderHeader()}
      sidebarContent={
        <AdherenceHistorySidebar
          closeSidebar={closeSidebar}
          preOpenedItemIds={preOpenedItemIds}
          setPreOpenedItemIds={setPreOpenedItemIds}
          setSidebarFilter={setSidebarFilter}
          sidebarFilter={sidebarFilter}
          teamsResult={{ data: teamsData } as SupervisedTeamsResult}
        />
      }
    >
      <Container data-test-id={CONTAINER_TAG} className="adherence-history">
        <LoadingOverlay loading={loading || lastImportLoading}>
          {(() => {
            if (loading || error) {
              return null;
            }

            if (!adherenceHistory.length) {
              return (
                <>
                  <ActiveFilters isFixed />
                  <FullSizeMessage
                    headline={
                      adherenceHistorySearchCriteria.agentName || !equals(defaultSidebarFilter, sidebarFilter)
                        ? t('filter.noResultsMatch')
                        : t('scheduleAdherence.noHistoryEntries')
                    }
                    iconKind="no_data"
                  />
                </>
              );
            }
            return (
              <>
                <ActiveFilters />
                <AdherenceHistoryTable
                  t={t}
                  adherenceHistory={adherenceHistory}
                  activeId={adherenceHistoryItemToView?.item?.agentId}
                  isOneDaySelected={isOneDaySelected}
                  onViewAdherenceHistoryItem={handleViewAdherenceHistoryItem}
                  teams={teamsAll}
                />
                <Pagination
                  activePage={activePage}
                  totalPages={totalPages}
                  setPage={setPage}
                  buttons={{
                    first: t('layout.pagination.first'),
                    previous: t('layout.pagination.previous'),
                    next: t('layout.pagination.next'),
                    last: t('layout.pagination.last'),
                  }}
                />
              </>
            );
          })()}
        </LoadingOverlay>
      </Container>
    </Content>
  );
};
